import type { Deal } from '@/hooks/use-deals';
import type { VerificationItem } from './types';

export type SendResult = { hash: string } | { conflict: Deal };

export interface SaveQueueHandlers {
  /** Persists the document, guarded by the fingerprint of the copy it was edited from. */
  send: (items: VerificationItem[], expectedHash: string) => Promise<SendResult>;
  onSaved: (notify: boolean) => void;
  onConflict: (latest: Deal) => void;
  onError: (error: unknown) => void;
}

/**
 * Sends verification document saves one at a time, each guarded by the
 * fingerprint of the last saved (or loaded) copy. Concurrent requests would
 * conflict with each other, since each save changes the fingerprint.
 *
 * Until `setBase` is called, the base never matches a real document, so a save
 * is rejected rather than allowed through. After a conflict nothing more is
 * sent until `setBase` is called for a freshly loaded copy.
 */
export class VerificationSaveQueue {
  private base: Promise<string> | string = '';
  private pending: { items: VerificationItem[]; notify: boolean } | null = null;
  private inFlight = false;
  private conflicted = false;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(private readonly handlers: SaveQueueHandlers, private readonly delayMs = 1000) {}

  setBase(hash: Promise<string> | string): void {
    this.base = hash;
    this.conflicted = false;
  }

  save(items: VerificationItem[], immediate = false): void {
    if (this.conflicted)
      return;
    this.pending = { items, notify: immediate || (this.pending?.notify ?? false) };
    clearTimeout(this.timer);
    if (immediate)
      void this.flush();
    else
      this.timer = setTimeout(() => void this.flush(), this.delayMs);
  }

  async flush(): Promise<void> {
    clearTimeout(this.timer);
    if (this.inFlight || this.conflicted || !this.pending)
      return;

    const { items, notify } = this.pending;
    this.pending = null;
    this.inFlight = true;
    try {
      const result = await this.handlers.send(items, await this.base);
      if ('conflict' in result) {
        this.conflicted = true;
        this.pending = null;
        this.handlers.onConflict(result.conflict);
      } else {
        this.base = result.hash;
        this.handlers.onSaved(notify);
      }
    } catch (error) {
      this.handlers.onError(error);
    } finally {
      this.inFlight = false;
    }

    if (this.pending)
      await this.flush();
  }
}
